import { PendingIssueListView } from '@euler/app/flows/common/components/PendingIssueListView';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { Aside } from '@euler/components/Aside';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileLayout,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ResourceAccessScope } from '@euler/model/enum';
import {
  PendingIssue,
  PendingIssueDetail,
  PendingIssueMedia,
} from '@euler/model/report';
import { useServiceFactory } from '@euler/services/factory';
import { useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const PendingIssuesPage = wrapNavigatorScreen(
  () => {
    const {
      taskNo,
      detail: { basicInfo },
    } = useTaskContext();
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const isMobile = useIsMobileLayout();
    const { licensePlateNo, vin } = basicInfo;
    const { vehicleInfoService } = useServiceFactory();
    const { state, loading, refreshing, error, invalidate } = useCachedQuery(
      `tasks/${taskNo}/pending-issues.json`,
      0,
      () =>
        vehicleInfoService.getPendingIssues({
          licensePlateNo,
          vin,
          scope: ResourceAccessScope.Org,
        }),
    );
    const pendingIssues = useMemo<PendingIssue[]>(
      () =>
        state?.map(({ issue, details, medias }) => ({
          name: issue.name,
          type: issue.type,
          defectiveLevel: issue.defectiveLevel,
          issueId: issue.issueId,
          details: details.map<PendingIssueDetail>(detail => ({
            detailId: detail.detailId,
            title: detail.title,
            isCustomIssue: detail.isCustomIssue,
            abnormalLevel: detail.abnormalLevel,
            severityLevel: detail.severityLevel,
            inspectionType: detail.inspectionType,
            siteId: detail.siteId ?? undefined,
            siteName: detail.siteName ?? undefined,
            siteCode: detail.siteCode ?? undefined,
            itemName: detail.itemName ?? undefined,
            itemId: detail.itemId ?? undefined,
            customIssueId: detail.customIssueId ?? undefined,
            authorId: detail.authorId ?? undefined,
            authorName: detail.authorName ?? undefined,
            inspectionResult: detail.inspectionResult ?? undefined,
            defectiveClass: detail.defectiveClass ?? undefined,
            remark: detail.remark ?? undefined,
            medias: medias
              .filter(x => x.detailId === detail.detailId)
              .map<PendingIssueMedia>(media => ({
                cid: media.cid,
                type: media.type,
                url: media.url,
                coverUrl: media.coverUrl ?? undefined,
                remark: media.remark ?? undefined,
              })),
          })),
        })) ?? [],
      [state],
    );

    if (isMobile) {
      return (
        <PendingIssueListView
          pendingIssues={pendingIssues}
          loading={loading}
          refreshing={refreshing}
          error={error}
          onRefresh={invalidate}
          contentContainerStyle={{
            paddingBottom: bottom + rpx(44),
          }}
        />
      );
    }

    return (
      <Aside dismissible>
        <LayoutProviderView
          css={`
            flex: 1;
          `}
        >
          <PendingIssueListView
            pendingIssues={pendingIssues}
            loading={loading}
            refreshing={refreshing}
            error={error}
            onRefresh={invalidate}
            contentContainerStyle={{
              paddingBottom: bottom + rpx(44),
            }}
          />
        </LayoutProviderView>
      </Aside>
    );
  },
  adaptiveWrapperOptions<any>(() => ({
    title: '历史遗留问题',
    headerStatusBarHeight: undefined,
    headerShown: isMobileLayout(),
  })),
);
